import React from 'react';
import { Info, Book, Shield, LayoutGrid, CheckCircle, AlertTriangle, Wind, Droplet } from 'lucide-react';

/**
 * Компонент с информацией о бетоне, типах, марках и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Book className="w-6 h-6 text-blue-500" />
        Всё о бетоне: марки, свойства, применение
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-blue-500" />
          Что такое бетон?
        </h3>
        <p>
          <strong>Бетон</strong> — это строительный материал, получаемый в результате затвердевания смеси цемента, воды, песка и щебня (или гравия). Прочность и другие характеристики бетона зависят от пропорций компонентов, качества составляющих, условий твердения и возраста бетона.
        </p>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Марки и классы бетона в России
        </h3>
        <p>
          В России применяются две системы обозначения прочности бетона: марка (обозначается буквой М) и класс (обозначается буквой В).
        </p>
        <ul>
          <li><strong>Марка бетона (М)</strong> указывает на предел прочности бетона на сжатие в кгс/см² через 28 дней твердения.</li>
          <li><strong>Класс бетона (В)</strong> определяет гарантированную прочность с обеспеченностью 95%, измеряется в МПа.</li>
        </ul>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-3 py-2 text-left">Марка</th>
                <th className="px-3 py-2 text-left">Класс</th>
                <th className="px-3 py-2 text-left">Применение</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М100</td>
                <td className="px-3 py-2 whitespace-nowrap">B7,5</td>
                <td className="px-3 py-2">Подготовительные работы, подбетонка, стяжки</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М150</td>
                <td className="px-3 py-2 whitespace-nowrap">B10</td>
                <td className="px-3 py-2">Дорожки, отмостки, неответственные конструкции</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М200</td>
                <td className="px-3 py-2 whitespace-nowrap">B15</td>
                <td className="px-3 py-2">Фундаменты малоэтажных домов, заборы, полы</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М250</td>
                <td className="px-3 py-2 whitespace-nowrap">B20</td>
                <td className="px-3 py-2">Фундаменты, монолитные стены, перекрытия</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М300</td>
                <td className="px-3 py-2 whitespace-nowrap">B22,5</td>
                <td className="px-3 py-2">Несущие конструкции, многоэтажные здания</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М350</td>
                <td className="px-3 py-2 whitespace-nowrap">B25-B27,5</td>
                <td className="px-3 py-2">Высоконагруженные конструкции, мосты</td>
              </tr>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">М400</td>
                <td className="px-3 py-2 whitespace-nowrap">B30</td>
                <td className="px-3 py-2">Гидротехнические сооружения, дороги</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-blue-500" />
          Состав и пропорции бетона
        </h3>
        <p>
          Классический состав бетона включает цемент, песок, щебень и воду. Пропорции компонентов зависят от требуемой марки бетона:
        </p>
        <ul>
          <li><strong>М100-М150:</strong> 1:4:6 (цемент:песок:щебень), В/Ц = 0,65-0,7</li>
          <li><strong>М200:</strong> 1:3:5, В/Ц = 0,55-0,6</li>
          <li><strong>М250-М300:</strong> 1:2:4, В/Ц = 0,5-0,55</li>
          <li><strong>М350-М400:</strong> 1:1,5:3, В/Ц = 0,4-0,45</li>
        </ul>
        <p>
          Водоцементное отношение (В/Ц) — это отношение массы воды к массе цемента. Чем ниже В/Ц, тем выше прочность бетона, но хуже удобоукладываемость.
        </p>

        <h3 className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Подготовка к заливке бетона
        </h3>
        <p>
          Правильная подготовка к бетонированию позволяет получить качественный результат и избежать проблем:
        </p>
        <ol>
          <li><strong>Подготовка основания</strong> — очистка, выравнивание, увлажнение.</li>
          <li><strong>Опалубка</strong> — должна быть прочной, герметичной и точно соответствовать проектным размерам.</li>
          <li><strong>Армирование</strong> — установка арматурных каркасов или сеток с соблюдением защитного слоя.</li>
          <li><strong>Подготовка инструментов</strong> — вибраторы, правила, лопаты, кельмы и т.д.</li>
          <li><strong>Планирование поставки</strong> — бетон должен быть уложен до начала схватывания (1-2 часа).</li>
        </ol>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Нормы расхода бетона для типовых конструкций</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li>Ленточный фундамент: 0,4-0,8 м³ на погонный метр (в зависимости от сечения)</li>
            <li>Плитный фундамент: 0,1-0,3 м³ на м² (зависит от толщины)</li>
            <li>Монолитная стена: 0,25-0,3 м³ на м² (при толщине 10-12 см)</li>
            <li>Монолитное перекрытие: 0,16-0,25 м³ на м² (при толщине 16-25 см)</li>
            <li>Стяжка пола: 0,08-0,1 м³ на м² (при толщине 8-10 см)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Wind className="w-5 h-5 text-blue-500" />
          Особенности зимнего бетонирования в России
        </h3>
        <p>
          В условиях российского климата часто приходится выполнять бетонные работы при отрицательных температурах, что требует особого подхода:
        </p>
        <ul>
          <li><strong>Противоморозные добавки</strong> — позволяют бетону набирать прочность при температуре до -15°C.</li>
          <li><strong>Прогрев бетона</strong> — применение специальных нагревательных проводов или инфракрасных обогревателей.</li>
          <li><strong>Тепляки</strong> — сооружение временных укрытий с тепловыми пушками.</li>
          <li><strong>Специальные марки цемента</strong> — быстротвердеющие и высокомарочные цементы.</li>
        </ul>
        <p>
          При температуре ниже -15°C рекомендуется отложить бетонирование или обратиться к специализированным компаниям с опытом зимних работ.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Распространенные ошибки при работе с бетоном
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Неправильный подбор марки бетона для конкретной конструкции</li>
            <li>Чрезмерное добавление воды для повышения подвижности смеси</li>
            <li>Недостаточное уплотнение бетона при укладке</li>
            <li>Отсутствие ухода за бетоном в процессе твердения</li>
            <li>Несоблюдение технологических перерывов при бетонировании</li>
            <li>Ошибки в расчете необходимого объема (без учета отходов)</li>
          </ul>
        </div>

        <h3>Уход за бетоном после заливки</h3>
        <p>
          Правильный уход за бетоном в первые дни после заливки во многом определяет его окончательную прочность и долговечность:
        </p>
        <ul>
          <li><strong>Поддержание влажности</strong> — бетон необходимо регулярно увлажнять в течение 7-14 дней для обеспечения нормальной гидратации цемента.</li>
          <li><strong>Защита от солнца и ветра</strong> — поверхность бетона следует укрывать пленкой или влажной мешковиной для предотвращения быстрого высыхания.</li>
          <li><strong>Температурный режим</strong> — оптимальная температура твердения +15...+25°C. При более низких температурах процесс замедляется.</li>
          <li><strong>Защита от механических воздействий</strong> — в первые дни бетон имеет низкую прочность и требует защиты от ударов и вибрации.</li>
        </ul>

        <h3>Экологические аспекты производства бетона</h3>
        <p>
          Производство цемента — ключевого компонента бетона — является энергоемким процессом и сопровождается значительными выбросами CO₂. В России и мире развиваются более экологичные подходы:
        </p>
        <ul>
          <li><strong>Использование добавок</strong> — шлаков, золы-уноса, микрокремнезема, что снижает долю цемента в составе.</li>
          <li><strong>Переработанные материалы</strong> — применение дробленого бетона в качестве крупного заполнителя.</li>
          <li><strong>Энергоэффективные технологии</strong> — модернизация цементных производств для снижения энергопотребления.</li>
          <li><strong>Инновационные вяжущие</strong> — разработка новых типов цемента с пониженным углеродным следом.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор объема бетона поможет вам точно рассчитать необходимое количество материала, избежать лишних затрат и спланировать бюджет строительного проекта!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;